import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import LeadSheetDetail from "./LeadSheetDetail";
import WComponent from "../../../../../common/WComponent";
import { fmtBal } from "../../shared/helpers/formatters";
import "../../afs-walkthrough.css";

class LeadSheetView extends WComponent {
	state = { leadSheets: [], fiscalYearEnd: null, loading: true };

	componentDidMount() {
		this.loadLeadSheets();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.clientId !== this.props.clientId) {
			this.loadLeadSheets();
		}
	}

	loadLeadSheets = async () => {
		this.update({ loading: true });
		let result = await BookkeepingService.getLeadSheets({ clientId: this.props.clientId });
		let next = { loading: false };
		if (result && result.leadSheets) next.leadSheets = result.leadSheets;
		if (result && result.fiscalYearEnd) next.fiscalYearEnd = result.fiscalYearEnd;
		this.update(next);
	};

	handleSignoff = async (wpRef, level) => {
		await BookkeepingService.updateLeadSheet({ clientId: this.props.clientId, wpRef, signoff: { level } });
		await this.loadLeadSheets();
	};

	handleSaveNotes = async (wpRef, notes) => {
		await BookkeepingService.updateLeadSheet({ clientId: this.props.clientId, wpRef, notes });
		await this.loadLeadSheets();
	};

	handleFarChange = async () => {
		// FAR mutation: refresh both the lead sheet view (so the Account table updates)
		// AND propagate to the parent so afsBalanceSheet/afsIncomeStatement refresh too.
		await this.loadLeadSheets();
		if (this.props.onFarChange) await this.props.onFarChange();
	};

	render() {
		let { clientId, selectedWpRef, onSelectWpRef } = this.props;
		let { leadSheets, fiscalYearEnd, loading } = this.state;
		let setSelectedWpRef = onSelectWpRef;

		let selected = selectedWpRef ? leadSheets.find(function (ls) { return ls.wpRef === selectedWpRef; }) : null;

		if (selected) {
			return (
				<LeadSheetDetail
					leadSheet={selected}
					clientId={clientId}
					fiscalYearEnd={fiscalYearEnd}
					onBack={function () { setSelectedWpRef(null); }}
					onSignoff={(level) => { this.handleSignoff(selected.wpRef, level); }}
					onSaveNotes={(notes) => { this.handleSaveNotes(selected.wpRef, notes); }}
					onFarChange={this.handleFarChange}
				/>
			);
		}

		let bsSheets = leadSheets.filter(function (ls) { return ls.statement === "BS"; });
		let isSheets = leadSheets.filter(function (ls) { return ls.statement === "IS"; });

		function getStatusIcon(ls) {
			if (ls.signoffs && ls.signoffs.reviewer) return { icon: Icon.CheckCircle, color: "#2e7d32", label: "Reviewed" };
			if (ls.signoffs && ls.signoffs.preparer) return { icon: Icon.CheckCircle, color: "#1565c0", label: "Prepared" };
			return { icon: Icon.Circle, color: "#ccc", label: "Open" };
		}

		function SheetTable({ sheets, title }) {
			if (sheets.length === 0) return null;
			return (
				<div className="afs-lead__group">
					<div className="afs-lead__group-title">{title}</div>
					<table className="afs-lead__table">
						<thead>
							<tr>
								<th className="afs-lead__th">WP Ref</th>
								<th className="afs-lead__th">Section</th>
								<th className="afs-lead__th afs-lead__th--right">Opening</th>
								<th className="afs-lead__th afs-lead__th--right">Adj.</th>
								<th className="afs-lead__th afs-lead__th--right">Closing</th>
								<th className="afs-lead__th afs-lead__th--center">Checks</th>
								<th className="afs-lead__th afs-lead__th--center">Status</th>
							</tr>
						</thead>
						<tbody>
							{sheets.map(function (ls) {
								let status = getStatusIcon(ls);
								let StatusIcon = status.icon;
								let failingChecks = ls.relatedChecks.filter(function (c) { return !c.passed; }).length;
								let totalChecks = ls.relatedChecks.length;
								return (
									<tr key={ls.wpRef} className="afs-lead__row" onClick={function () { setSelectedWpRef(ls.wpRef); }}>
										<td className="afs-lead__td afs-lead__td--ref">{ls.wpRef}</td>
										<td className="afs-lead__td">
											<span className="afs-lead__td-title">{ls.title}</span>
											<span className="afs-lead__td-count">{ls.accountCount} account(s)</span>
										</td>
										<td className="afs-lead__td afs-lead__td--right afs-lead__td--mono">{fmtBal(ls.totals.opening)}</td>
										<td className={clsx("afs-lead__td", "afs-lead__td--right", "afs-lead__td--mono", ls.totals.adjustments !== 0 && "afs-lead__td--adj")}>{ls.totals.adjustments !== 0 ? fmtBal(ls.totals.adjustments) : "—"}</td>
										<td className="afs-lead__td afs-lead__td--right afs-lead__td--mono afs-lead__td--bold">{fmtBal(ls.totals.closing)}</td>
										<td className="afs-lead__td afs-lead__td--center">
											{totalChecks > 0 ? (
												<span className={clsx("afs-lead__checks-badge", failingChecks > 0 ? "afs-lead__checks-badge--fail" : "afs-lead__checks-badge--pass")}>
													{failingChecks > 0 ? failingChecks + " issue(s)" : totalChecks + " passed"}
												</span>
											) : <span className="afs-lead__checks-badge">—</span>}
										</td>
										<td className="afs-lead__td afs-lead__td--center">
											<StatusIcon size={16} color={status.color} />
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			);
		}

		return (
			<div className="afs-lead">
				{loading ? (
					<div className="afs-lead__loading">Loading lead sheets...</div>
				) : leadSheets.length === 0 ? (
					<div className="afs-lead__empty">
						<Icon.FileText size={32} color="#ccc" />
						<p>No lead sheet data available. Run a compilation first.</p>
					</div>
				) : (
					<>
						<SheetTable sheets={bsSheets} title="Balance Sheet" />
						<SheetTable sheets={isSheets} title="Income Statement" />
					</>
				)}
			</div>
		);
	}
}

export default LeadSheetView;
